import { z } from 'zod';

/** Holidays with lobby decorations (for every language). Only decoration — payouts never change. */
export const SEASON_IDS = ['spring-festival', 'lantern', 'dragon-boat', 'mid-autumn'] as const;
export type SeasonId = (typeof SEASON_IDS)[number];

/** Calendar date "YYYY-MM-DD" (China Standard Time for holidays). */
export type ISODate = string;
export const zISODate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const zSeason = z
  .object({ id: z.enum(SEASON_IDS), from: zISODate, to: zISODate })
  .strict()
  .refine((s) => s.from <= s.to, { message: 'from after to', path: ['to'] });
/** Active window, inclusive on both ends, in Beijing calendar days. */
export type Season = z.infer<typeof zSeason>;

/** GET /api/lobby/season */
export const zSeasonResponse = z.object({ season: zSeason.nullable() });
export interface SeasonResponse {
  season: Season | null;
}

/**
 * Lunar New Year (1st day of the 1st lunar month), Gregorian date in China Standard Time.
 * A table on purpose — the lunar calendar is not computed at runtime.
 */
export const LUNAR_NEW_YEAR: Readonly<Record<number, ISODate>> = {
  2025: '2025-01-29',
  2026: '2026-02-17',
  2027: '2027-02-06',
  2028: '2028-01-26',
  2029: '2029-02-13',
  2030: '2030-02-03',
  2031: '2031-01-23',
};

const BEIJING_OFFSET_MS = 8 * 3600_000;

/** Calendar day of an instant in Beijing (UTC+8, no DST). */
export function beijingDate(now: Date): ISODate {
  return new Date(now.getTime() + BEIJING_OFFSET_MS).toISOString().slice(0, 10);
}

/** The 12 animals in cycle order; 2020 was a Rat year. */
export const ZODIAC = ['rat', 'ox', 'tiger', 'rabbit', 'dragon', 'snake', 'horse', 'goat', 'monkey', 'rooster', 'dog', 'pig'] as const;
export type ZodiacAnimal = (typeof ZODIAC)[number];

/**
 * Animal of the lunar year containing `now` (Beijing day). null outside the table: before its first new year,
 * or in the last listed year (its end is not in the table) — better no badge than a wrong one.
 */
export function zodiacOf(now: Date): ZodiacAnimal | null {
  const day = beijingDate(now);
  const years = Object.keys(LUNAR_NEW_YEAR)
    .map(Number)
    .sort((a, b) => a - b);
  let year: number | null = null;
  for (const y of years) if ((LUNAR_NEW_YEAR[y] as ISODate) <= day) year = y;
  if (year === null || year === years[years.length - 1]) return null;
  return ZODIAC[(((year - 2020) % 12) + 12) % 12] as ZodiacAnimal;
}
